/* eslint-disable consistent-return */
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'
import type { Feature } from 'geojson'
import L, { PM } from 'leaflet'
import { useEffect, useRef } from 'react'
import { FeatureGroup } from 'react-leaflet'
import { GeomanControls as ReactLeafletGeomanControls } from 'react-leaflet-geoman-v2'
import { GEOMAN_OPTIONS } from '../../utils/constants'
import { useMapContext } from './MapContext'

interface GeoJSONLayer extends L.Layer {
  toGeoJSON(): Feature
}

interface GeomanCreateEvent {
  layer: GeoJSONLayer
  shape: string
  workingLayer: L.Layer
}

interface GeomanEditEvent {
  layer: GeoJSONLayer
  shape: string
}

interface GeomanRemoveEvent {
  layer: GeoJSONLayer
  shape: string
}

export type GeomanCreateEventHandler = PM.CreateEventHandler & ((e: GeomanCreateEvent) => void)
export type GeomanEditEventHandler = PM.EditEventHandler & ((e: GeomanEditEvent) => void)
export type GeomanRemoveEventHandler = PM.RemoveEventHandler & ((e: GeomanRemoveEvent) => void)

export interface GeomanControlsProps {
  position?: 'topleft' | 'topright' | 'bottomleft' | 'bottomright'
  drawControls?: boolean
  editControls?: boolean
  optionsControls?: boolean
  customControls?: boolean
  continueDrawing?: boolean
  allowSelfIntersection?: boolean
  snapDistance?: number
  limitMarkersToViewport?: boolean
  isDrawingMode?: boolean
  editingFeatureId?: string | null
  getFeatureId?: (feature: Feature) => string | null
  onCreate?: GeomanCreateEventHandler
  onEdit?: GeomanEditEventHandler
  onRemove?: GeomanRemoveEventHandler
  onKeyEvent?: PM.KeyboardKeyEventHandler
  onCancel?: () => void
  globalOptions?: PM.GlobalOptions
  lang?: PM.SupportLocales
  children?: React.ReactNode
}

const EMPTY_OPTIONS: PM.GlobalOptions = {}

export const GeomanControls: React.FC<GeomanControlsProps> = ({
  position = 'topleft',
  drawControls = false,
  editControls = false,
  optionsControls = false,
  customControls = false,
  continueDrawing = false,
  allowSelfIntersection = false,
  snapDistance = 5,
  limitMarkersToViewport = true,
  isDrawingMode = false,
  editingFeatureId = null,
  getFeatureId,
  onCreate,
  onEdit,
  onRemove,
  onKeyEvent,
  onCancel,
  globalOptions = EMPTY_OPTIONS,
  lang = 'es',
  children,
}) => {
  const featureGroupRef = useRef<L.FeatureGroup>(null)
  const { map } = useMapContext()

  useEffect(() => {
    if (!map || !onEdit) return

    map.on('pm:edit', onEdit)
    return () => {
      map.off('pm:edit', onEdit)
    }
  }, [map, onEdit])

  useEffect(() => {
    if (!map || !onRemove) return

    map.on('pm:remove', onRemove)
    return () => {
      map.off('pm:remove', onRemove)
    }
  }, [map, onRemove])

  useEffect(() => {
    if (map && isDrawingMode) {
      map.pm.enableDraw('Polygon', {
        allowSelfIntersection,
        continueDrawing,
        snapDistance,
      })

      return () => {
        map.pm.disableDraw()
      }
    }

    if (map && !isDrawingMode) {
      map.pm.disableDraw()
    }
  }, [map, isDrawingMode, allowSelfIntersection, continueDrawing, snapDistance])

  useEffect(() => {
    if (!map || !editingFeatureId || !getFeatureId) return

    // Small delay to ensure layers are mounted
    const timer = setTimeout(() => {
      map.eachLayer((layer: L.Layer & { feature?: Feature; pm?: any }) => {
        if (layer.feature && layer.pm) {
          const layerId = getFeatureId(layer.feature)

          if (layerId === editingFeatureId) {
            layer.pm.enable({
              allowSelfIntersection: false,
              preventMarkerRemoval: false,
            })
            if (onEdit) {
              layer.on('pm:markerdragend', onEdit)
            }
          } else {
            layer.pm.disable()
            if (onEdit) {
              layer.off('pm:markerdragend', onEdit)
            }
          }
        }
      })
    }, 50)

    return () => {
      clearTimeout(timer)
      if (map) {
        map.eachLayer((layer: L.Layer & { pm?: any }) => {
          if (layer.pm) {
            layer.pm.disable()
            if (onEdit) {
              layer.off('pm:markerdragend', onEdit)
            }
          }
        })
      }
    }
  }, [map, editingFeatureId, getFeatureId, onEdit])

  const defaultGlobalOptions: PM.GlobalOptions = {
    limitMarkersToCount: GEOMAN_OPTIONS.limitMarkersToCount,
    limitMarkersToViewport,
    continueDrawing,
    snapDistance,
    allowSelfIntersection,
    layerGroup: featureGroupRef.current,
    ...globalOptions,
  }

  const handleKeyEvent = e => {
    if (e.event.type === 'keyup' && e.event.key === 'Escape') {
      if (onCancel) {
        onCancel()
      } else {
        map.pm.disableDraw()
      }
    }
  }

  return (
    <FeatureGroup ref={featureGroupRef}>
      <ReactLeafletGeomanControls
        globalOptions={defaultGlobalOptions}
        lang={lang}
        options={{
          position,
          drawControls,
          editControls,
          optionsControls,
          customControls,
        }}
        onCreate={onCreate}
        onEdit={onEdit}
        onKeyEvent={onKeyEvent || handleKeyEvent}
        onLayerRemove={onRemove}
      />
      {children}
    </FeatureGroup>
  )
}
